import React, { useEffect, useState } from 'react';
import { Dealer } from '@/types/dealer';

interface StaticMapProps {
  center: [number, number];
  zoom: number;
  dealers: Dealer[];
  userPosition?: GeolocationPosition | null;
  height?: string;
  width?: string;
  mapId?: string;
}

export function StaticMap({
  center,
  zoom,
  dealers,
  userPosition,
  height = '400px',
  width = '100%'
}: StaticMapProps) {
  const [mapComponents, setMapComponents] = useState<any>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadLeaflet = async () => {
      try {
        // Carica leaflet e react-leaflet
        const [leafletModule, reactLeafletModule] = await Promise.all([
          import('leaflet'),
          import('react-leaflet')
        ]);

        if (!mounted) return;

        // Importa il CSS
        await import('leaflet/dist/leaflet.css');

        if (!mounted) return;

        // Importa i componenti custom
        const [
          { UserMarker },
          { DealerMarkers }
        ] = await Promise.all([
          import('./UserMarker'),
          import('./MapMarkers')
        ]);

        if (!mounted) return;

        setMapComponents({
          MapContainer: reactLeafletModule.MapContainer,
          TileLayer: reactLeafletModule.TileLayer,
          UserMarker,
          DealerMarkers
        });

        console.log('✅ Static map components loaded');
      } catch (error) {
        console.error('❌ Error loading static map components:', error);
      }
    };

    loadLeaflet();

    return () => {
      mounted = false;
    };
  }, []);

  if (!mapComponents) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-100 rounded-lg animate-pulse"
        style={{ height, width }}
      >
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Caricamento mappa...</p>
        </div>
      </div>
    );
  }

  const { 
    MapContainer, 
    TileLayer, 
    UserMarker, 
    DealerMarkers 
  } = mapComponents;

  return (
    <div className="relative" style={{ height, width }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height, width }}
        className="rounded-lg"
        // Configurazioni per mappa statica - disabilita tutte le interazioni
        dragging={false}
        touchZoom={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        boxZoom={false}
        keyboard={false}
        zoomControl={false}
        attributionControl={false}
        whenReady={() => {
          console.log('✅ Static map ready');
          setMapReady(true);
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          maxZoom={18}
        />
        
        {/* Marker utente */}
        {userPosition && (
          <UserMarker
            position={[userPosition.coords.latitude, userPosition.coords.longitude]}
          />
        )}
        
        {/* Marker dealer - senza onDealerSelect */}
        <DealerMarkers
          dealers={dealers}
        />
      </MapContainer>
      
      {/* Overlay per indicare che la mappa non è interattiva */}
      <div 
        className="absolute inset-0 bg-transparent cursor-default rounded-lg"
        style={{ pointerEvents: 'none' }}
        title="Mappa non interattiva"
      />
    </div>
  );
}
